import { useState, useMemo, useRef, useCallback, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery } from 'convex/react'
import { api } from '../../../../../convex/_generated/api'
import { PlyrPlayer } from '@/components/video'
import { cn } from '@/lib/utils'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {
  ArrowLeft,
  ArrowDown,
  ArrowUp,
  ChevronDown,
  ChevronRight,
  Download,
  ExternalLink,
  Eye,
  EyeOff,
  Film,
  FolderOpen,
  Loader2,
  MessageSquare,
  PanelRightClose,
  PanelRightOpen,
  Search,
  User,
  Bot,
  Wrench,
  X,
  Image as ImageIcon,
  FileText,
  Play,
  Volume2,
  Cpu,
  Database,
  Layers,
  WrapText,
  Star,
  MessageCircle,
} from 'lucide-react'
import { WorkflowDocument } from '@/components/workflow/WorkflowDocument'
import { ErrorBoundary } from '@/components/ErrorBoundary'

// ─── Constants ──────────────────────────────────────────────────────────────

const PLAN_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  admin: { bg: 'bg-red-500/10', text: 'text-red-400', label: 'Admin' },
  free: { bg: 'bg-neutral-500/10', text: 'text-neutral-400', label: 'Free' },
  pro: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', label: 'Pro' },
  max: { bg: 'bg-violet-500/10', text: 'text-violet-400', label: 'Max' },
  ultra: { bg: 'bg-fuchsia-500/10', text: 'text-fuchsia-400', label: 'Ultra' },
}

const TOOL_CATEGORY_COLORS: Record<string, string> = {
  Search: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
  Project: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  Timeline: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  Generation: 'bg-purple-500/15 text-purple-300 border-purple-500/30',
  Analysis: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30',
  Export: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  Billing: 'bg-orange-500/15 text-orange-300 border-orange-500/30',
  Assets: 'bg-teal-500/15 text-teal-300 border-teal-500/30',
  Audio: 'bg-pink-500/15 text-pink-300 border-pink-500/30',
  'Post-Production': 'bg-indigo-500/15 text-indigo-300 border-indigo-500/30',
  Media: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
  Desktop: 'bg-stone-500/15 text-stone-300 border-stone-500/30',
  Visual: 'bg-violet-500/15 text-violet-300 border-violet-500/30',
  NUX: 'bg-lime-500/15 text-lime-300 border-lime-500/30',
}

const MODEL_SHORT: Record<string, string> = {
  'claude-opus-4-5-20251101': 'Opus 4.5',
  'claude-opus-4-6': 'Opus 4.6',
  'claude-sonnet-4-5-20250929': 'Sonnet 4.5',
  'claude-sonnet-4-5': 'Sonnet 4.5',
  'claude-sonnet-4-6': 'Sonnet 4.6',
  'claude-sonnet-4-20250514': 'Sonnet 4',
  'claude-haiku-4-5-20251001': 'Haiku 4.5',
  'gemini-2.0-flash': 'Gemini Flash',
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatTime(ts: number): string {
  return new Date(ts).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit' })
}

function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function formatDuration(ms: number): string {
  if (ms <= 0) return '--'
  const totalSec = Math.floor(ms / 1000)
  const h = Math.floor(totalSec / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = totalSec % 60
  if (h > 0) return `${h}h ${m}m ${s}s`
  if (m > 0) return `${m}m ${s}s`
  return `${s}s`
}

function formatDelta(ms: number): string {
  if (ms < 1000) return '<1s'
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ${Math.round((ms % 60_000) / 1000)}s`
  return `${Math.floor(ms / 3_600_000)}h ${Math.floor((ms % 3_600_000) / 60_000)}m`
}

function formatTokens(count: number): string {
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`
  if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`
  return count.toString()
}

function truncate(s: string, max: number): string {
  return s.length > max ? s.slice(0, max) + '...' : s
}

function guessToolCategory(name: string): string {
  if (/search|find|get_asset/i.test(name)) return 'Search'
  if (/set_title|update_narrative|update_settings|set_aspect/i.test(name)) return 'Project'
  if (/add_clip|remove_clip|reorder|update_clip|move_clip|split_clip|merge_clip|duplicate_clip/i.test(name)) return 'Timeline'
  if (/generate|regenerate|start_generation/i.test(name)) return 'Generation'
  if (/analy[sz]|review_(video|timeline)|evaluate/i.test(name)) return 'Analysis'
  if (/export|render/i.test(name)) return 'Export'
  if (/credit|upgrade|billing|subscribe/i.test(name)) return 'Billing'
  if (/asset|upload|media/i.test(name)) return 'Assets'
  if (/audio|tts|voice|music|sound/i.test(name)) return 'Audio'
  if (/transition|effect|color_grade|filter/i.test(name)) return 'Post-Production'
  if (/show_media|show_cluster|show_folder/i.test(name)) return 'Visual'
  if (/nux|complete_nux/i.test(name)) return 'NUX'
  return 'Other'
}

// ─── JSON Viewer ────────────────────────────────────────────────────────────

function JsonValue({ value, depth = 0 }: { value: unknown; depth?: number }) {
  const [expanded, setExpanded] = useState(depth < 2)

  if (value === null || value === undefined) {
    return <span className="text-neutral-500 italic">{String(value)}</span>
  }
  if (typeof value === 'boolean') {
    return <span className="text-amber-400">{String(value)}</span>
  }
  if (typeof value === 'number') {
    return <span className="text-cyan-400">{value}</span>
  }
  if (typeof value === 'string') {
    // URL detection
    if (/^https?:\/\//.test(value)) {
      return (
        <a href={value} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline break-all">
          "{value}"
        </a>
      )
    }
    return <span className="text-green-400 break-all whitespace-pre-wrap">"{value}"</span>
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-neutral-500">[]</span>
    return (
      <span>
        <button onClick={() => setExpanded(!expanded)} className="text-neutral-500 hover:text-neutral-300">
          {expanded ? '▼' : '▶'} [{value.length}]
        </button>
        {expanded && (
          <div className="ml-4 border-l border-neutral-700/50 pl-3">
            {value.map((item, i) => (
              <div key={i} className="flex">
                <span className="text-neutral-600 mr-2 select-none shrink-0">{i}:</span>
                <JsonValue value={item} depth={depth + 1} />
              </div>
            ))}
          </div>
        )}
      </span>
    )
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
    if (entries.length === 0) return <span className="text-neutral-500">{'{}'}</span>
    return (
      <span>
        <button onClick={() => setExpanded(!expanded)} className="text-neutral-500 hover:text-neutral-300">
          {expanded ? '▼' : '▶'} {'{'}
          {!expanded && <span className="text-neutral-600">{entries.length} keys</span>}
          {!expanded && '}'}
        </button>
        {expanded && (
          <div className="ml-4 border-l border-neutral-700/50 pl-3">
            {entries.map(([key, val]) => (
              <div key={key} className="flex">
                <span className="text-violet-400 mr-1 shrink-0">"{key}":</span>
                <JsonValue value={val} depth={depth + 1} />
              </div>
            ))}
          </div>
        )}
        {expanded && <span className="text-neutral-500">{'}'}</span>}
      </span>
    )
  }

  return <span className="text-neutral-400">{String(value)}</span>
}

// ─── Tool Call Card ─────────────────────────────────────────────────────────

interface ToolCallData {
  id: string
  name: string
  input: any
}

interface ToolResultData {
  toolCallId: string
  result: any
}

function ToolCallCard({
  call,
  result,
  index,
  searchQuery,
  wrapLines,
}: {
  call: ToolCallData
  result?: ToolResultData
  index: number
  searchQuery: string
  wrapLines?: boolean
}) {
  const [expanded, setExpanded] = useState(false)
  const category = guessToolCategory(call.name)
  const catColor = TOOL_CATEGORY_COLORS[category] ?? 'bg-neutral-500/15 text-neutral-400 border-neutral-500/30'
  const hasResult = !!result
  const resultSuccess = result?.result?.success !== false
  const matchesSearch = searchQuery && (
    call.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    JSON.stringify(call.input).toLowerCase().includes(searchQuery.toLowerCase()) ||
    JSON.stringify(result?.result).toLowerCase().includes(searchQuery.toLowerCase())
  )

  return (
    <div className={cn(
      'rounded-md border transition-colors',
      matchesSearch ? 'border-yellow-500/50 bg-yellow-500/5' : 'border-neutral-700/50 bg-neutral-800/30',
    )}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-2.5 py-1.5 text-left hover:bg-neutral-700/20 rounded-md"
      >
        <span className="text-neutral-600 text-[10px] font-mono w-4 shrink-0">#{index + 1}</span>
        {expanded ? <ChevronDown className="h-3 w-3 text-neutral-500 shrink-0" /> : <ChevronRight className="h-3 w-3 text-neutral-500 shrink-0" />}
        <Wrench className="h-3 w-3 text-neutral-400 shrink-0" />
        <span className="text-xs font-medium text-neutral-200 font-mono">{call.name}</span>
        <span className={cn('text-[9px] px-1.5 py-0.5 rounded border shrink-0', catColor)}>{category}</span>
        <span className="flex-1" />
        {hasResult && (
          <span className={cn('text-[10px] px-1.5 py-0.5 rounded', resultSuccess ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400')}>
            {resultSuccess ? 'OK' : 'FAIL'}
          </span>
        )}
      </button>
      {expanded && (
        <div className="px-3 pb-2.5 space-y-2">
          {/* Input */}
          <div>
            <div className="text-[10px] uppercase tracking-wide text-neutral-500 mb-1">Input</div>
            <div className={cn("rounded bg-neutral-900/80 p-2 font-mono text-[11px] max-h-[300px] overflow-y-auto", wrapLines ? 'whitespace-pre-wrap break-all' : 'overflow-x-auto')}>
              <JsonValue value={call.input} />
            </div>
          </div>
          {/* Result — show what the model sees (without echoed input) */}
          {result && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-neutral-500 mb-1">Result</div>
              <div className={cn("rounded bg-neutral-900/80 p-2 font-mono text-[11px] max-h-[300px] overflow-y-auto", wrapLines ? 'whitespace-pre-wrap break-all' : 'overflow-x-auto')}>
                <JsonValue value={(() => { const { input: _, ...rest } = (result.result ?? {}) as Record<string, unknown>; return rest; })()} />
              </div>
            </div>
          )}
          {/* Raw ID */}
          <div className="text-[10px] text-neutral-600 font-mono">id: {call.id}</div>
        </div>
      )}
    </div>
  )
}

// ─── Message Block ──────────────────────────────────────────────────────────

interface MessageData {
  _id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  createdAt: number
  toolCalls?: ToolCallData[]
  toolResults?: ToolResultData[]
  attachments?: Array<{ type: string; url: string; name?: string; duration?: number; mimeType?: string; extractedText?: string }>
  suggestedActions?: string[]
  isStreaming: boolean
}

const ROLE_CONFIG = {
  user: { icon: User, label: 'USER', color: 'text-cyan-400', bg: 'bg-cyan-500/8', border: 'border-cyan-500/20' },
  assistant: { icon: Bot, label: 'AGENT', color: 'text-emerald-400', bg: 'bg-emerald-500/5', border: 'border-emerald-500/15' },
  system: { icon: Cpu, label: 'SYSTEM', color: 'text-amber-400', bg: 'bg-amber-500/5', border: 'border-amber-500/15' },
}

function MessageBlock({
  msg,
  index,
  prevTimestamp,
  collapsed,
  onToggleCollapse,
  searchQuery,
  isHighlighted,
  wrapLines,
}: {
  msg: MessageData
  index: number
  prevTimestamp: number | null
  collapsed: boolean
  onToggleCollapse: () => void
  searchQuery: string
  isHighlighted: boolean
  wrapLines?: boolean
}) {
  const config = ROLE_CONFIG[msg.role]
  const Icon = config.icon
  const delta = prevTimestamp ? msg.createdAt - prevTimestamp : 0
  const toolCalls = msg.toolCalls || []
  const toolResults = msg.toolResults || []
  const resultMap = new Map(toolResults.map(r => [r.toolCallId, r]))
  const hasTools = toolCalls.length > 0
  const contentMatchesSearch = searchQuery && msg.content.toLowerCase().includes(searchQuery.toLowerCase())
  const attachments = (msg.attachments || []) as MessageData['attachments'] & Array<any>

  // Split content around tool calls if there's content after tools
  const content = msg.content

  return (
    <div
      id={`msg-${msg._id}`}
      className={cn(
        'group border-l-2 transition-all',
        config.border,
        isHighlighted && 'ring-1 ring-yellow-500/40 bg-yellow-500/5',
        contentMatchesSearch && 'bg-yellow-500/5',
      )}
    >
      {/* Time delta */}
      {delta > 2000 && (
        <div className="flex items-center gap-2 px-4 py-1">
          <div className="flex-1 h-px bg-neutral-800" />
          <span className="text-[10px] text-neutral-600 font-mono">{formatDelta(delta)}</span>
          <div className="flex-1 h-px bg-neutral-800" />
        </div>
      )}

      {/* Header */}
      <div
        className={cn('flex items-center gap-2 px-4 py-1.5 cursor-pointer select-none hover:bg-neutral-800/30', config.bg)}
        onClick={onToggleCollapse}
      >
        <span className="text-[10px] text-neutral-600 font-mono w-8 text-right shrink-0">#{index + 1}</span>
        <Icon className={cn('h-3.5 w-3.5 shrink-0', config.color)} />
        <span className={cn('text-[11px] font-bold tracking-wide', config.color)}>{config.label}</span>
        <span className="text-[10px] text-neutral-600 font-mono">{formatTime(msg.createdAt)}</span>
        {hasTools && (
          <span className="text-[10px] text-neutral-500 flex items-center gap-1">
            <Wrench className="h-2.5 w-2.5" />
            {toolCalls.length} tool{toolCalls.length > 1 ? 's' : ''}
          </span>
        )}
        {attachments && attachments.length > 0 && (
          <span className="text-[10px] text-neutral-500 flex items-center gap-1">
            <ImageIcon className="h-2.5 w-2.5" />
            {attachments.length}
          </span>
        )}
        {msg.isStreaming && (
          <span className="text-[10px] text-amber-400 flex items-center gap-1">
            <Loader2 className="h-2.5 w-2.5 animate-spin" /> streaming
          </span>
        )}
        <span className="flex-1" />
        {msg.role === 'assistant' && (
          collapsed
            ? <ChevronRight className="h-3 w-3 text-neutral-600" />
            : <ChevronDown className="h-3 w-3 text-neutral-600" />
        )}
        {collapsed && content && (
          <span className="text-[11px] text-neutral-600 truncate max-w-[400px]">
            {truncate(content, 80)}
          </span>
        )}
      </div>

      {/* Body */}
      {!collapsed && (
        <div className="px-4 pb-3 space-y-2">
          {/* Attachments */}
          {attachments && attachments.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-1">
              {attachments.map((att: any, i: number) => (
                <AttachmentBadge key={i} attachment={att} />
              ))}
            </div>
          )}

          {/* Content */}
          {content && (
            <div className="text-[13px] text-neutral-300 leading-relaxed prose prose-invert prose-sm max-w-none
              prose-p:my-1 prose-pre:bg-neutral-900 prose-pre:text-[11px] prose-code:text-[11px]
              prose-headings:text-neutral-200 prose-headings:text-sm prose-strong:text-neutral-200
              prose-ul:my-1 prose-li:my-0.5">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
            </div>
          )}

          {/* Tool Calls */}
          {hasTools && (
            <div className="space-y-1 pt-1">
              {toolCalls.map((tc, i) => (
                <ToolCallCard
                  key={tc.id}
                  call={tc}
                  result={resultMap.get(tc.id)}
                  index={i}
                  searchQuery={searchQuery}
                  wrapLines={wrapLines}
                />
              ))}
            </div>
          )}

          {/* Suggested Actions */}
          {msg.suggestedActions && msg.suggestedActions.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {msg.suggestedActions.map((action, i) => (
                <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-neutral-700/50 text-neutral-400 border border-neutral-600/30">
                  {action}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Attachment Badge ───────────────────────────────────────────────────────

function AttachmentBadge({ attachment }: { attachment: any }) {
  const iconMap: Record<string, any> = {
    image: ImageIcon,
    video: Play,
    audio: Volume2,
    document: FileText,
  }
  const Icon = iconMap[attachment.type] || FileText
  return (
    <a
      href={attachment.url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-1.5 px-2 py-1 rounded bg-neutral-800/50 border border-neutral-700/50 hover:border-neutral-600 text-[11px] text-neutral-400 hover:text-neutral-300 transition-colors"
    >
      <Icon className="h-3 w-3" />
      <span>{attachment.name || attachment.type}</span>
      {attachment.duration && <span className="text-neutral-600">({Math.round(attachment.duration)}s)</span>}
    </a>
  )
}

// ─── Clip Inspector ─────────────────────────────────────────────────────────

function ClipInspector({ clips }: { clips: any[] }) {
  const [expandedClip, setExpandedClip] = useState<string | null>(null)

  return (
    <div className="space-y-1">
      {clips.map((clip, i) => {
        const isExpanded = expandedClip === clip._id
        const statusColor = clip.videoStatus === 'completed' ? 'bg-emerald-500'
          : clip.videoStatus === 'failed' ? 'bg-red-500'
          : clip.videoStatus?.startsWith('generating') ? 'bg-amber-500 animate-pulse'
          : 'bg-neutral-600'

        return (
          <div key={clip._id} className="rounded border border-neutral-700/50 bg-neutral-800/30">
            <button
              onClick={() => setExpandedClip(isExpanded ? null : clip._id)}
              className="w-full flex items-center gap-2 px-2.5 py-2 text-left hover:bg-neutral-700/20"
            >
              {clip.firstFrameUrl ? (
                <img src={clip.firstFrameUrl} alt="" className="w-12 h-8 rounded object-cover shrink-0 bg-neutral-900" />
              ) : (
                <div className="w-12 h-8 rounded bg-neutral-900 flex items-center justify-center shrink-0">
                  <Film className="h-3 w-3 text-neutral-700" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-neutral-600 font-mono">#{i + 1}</span>
                  <span className="text-xs text-neutral-300 truncate">{clip.title || clip.promptSummary || 'Untitled'}</span>
                </div>
                <div className="flex items-center gap-2 text-[10px] text-neutral-500">
                  <span>{clip.duration}s</span>
                  {clip.generationMode && <span className="text-neutral-600">{clip.generationMode}</span>}
                </div>
              </div>
              <div className={cn('w-2 h-2 rounded-full shrink-0', statusColor)} />
              {isExpanded ? <ChevronDown className="h-3 w-3 text-neutral-600" /> : <ChevronRight className="h-3 w-3 text-neutral-600" />}
            </button>
            {isExpanded && (
              <div className="px-3 pb-3 space-y-2 border-t border-neutral-700/30">
                {clip.videoPrompt && (
                  <div className="pt-2">
                    <div className="text-[10px] uppercase tracking-wide text-neutral-500 mb-1">Video Prompt</div>
                    <div className="text-[11px] text-neutral-400 bg-neutral-900/50 p-2 rounded font-mono whitespace-pre-wrap max-h-[200px] overflow-y-auto">
                      {clip.videoPrompt}
                    </div>
                  </div>
                )}
                {clip.videoUrl && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-neutral-500 mb-1">Video</div>
                    <a href={clip.videoUrl} target="_blank" rel="noopener noreferrer" className="text-[11px] text-blue-400 hover:underline break-all">
                      {truncate(clip.videoUrl, 80)}
                    </a>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2 text-[11px]">
                  <div><span className="text-neutral-500">Status:</span> <span className="text-neutral-300">{clip.videoStatus}</span></div>
                  <div><span className="text-neutral-500">Duration:</span> <span className="text-neutral-300">{clip.duration}s</span></div>
                  <div><span className="text-neutral-500">Order:</span> <span className="text-neutral-300">{clip.orderIndex}</span></div>
                  <div><span className="text-neutral-500">Mode:</span> <span className="text-neutral-300">{clip.generationMode || 'N/A'}</span></div>
                  {clip.ttsStatus && clip.ttsStatus !== 'none' && (
                    <div><span className="text-neutral-500">TTS:</span> <span className="text-neutral-300">{clip.ttsStatus}</span></div>
                  )}
                  {clip.lipSyncStatus && clip.lipSyncStatus !== 'none' && (
                    <div><span className="text-neutral-500">LipSync:</span> <span className="text-neutral-300">{clip.lipSyncStatus}</span></div>
                  )}
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ─── LLM Usage Panel ────────────────────────────────────────────────────────

function LlmUsagePanel({ usage }: { usage: any[] }) {
  const totalInput = usage.reduce((s, r) => s + (r.inputTokens || 0), 0)
  const totalOutput = usage.reduce((s, r) => s + (r.outputTokens || 0), 0)
  const totalCache = usage.reduce((s, r) => s + (r.cacheReadTokens || 0), 0)
  const features = [...new Set(usage.map(r => r.feature))].sort()
  const models = [...new Set(usage.map(r => r.model))].sort()

  return (
    <div className="space-y-3">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-2">
        <div className="rounded bg-neutral-800/50 p-2 text-center">
          <div className="text-[10px] text-neutral-500">Input</div>
          <div className="text-sm font-medium text-neutral-200">{formatTokens(totalInput)}</div>
        </div>
        <div className="rounded bg-neutral-800/50 p-2 text-center">
          <div className="text-[10px] text-neutral-500">Output</div>
          <div className="text-sm font-medium text-neutral-200">{formatTokens(totalOutput)}</div>
        </div>
        <div className="rounded bg-neutral-800/50 p-2 text-center">
          <div className="text-[10px] text-neutral-500">Cache</div>
          <div className="text-sm font-medium text-neutral-200">{formatTokens(totalCache)}</div>
        </div>
      </div>

      {/* Feature breakdown */}
      <div>
        <div className="text-[10px] uppercase tracking-wide text-neutral-500 mb-1.5">By Feature</div>
        <div className="space-y-0.5">
          {features.map(f => {
            const rows = usage.filter(r => r.feature === f)
            const inp = rows.reduce((s, r) => s + (r.inputTokens || 0), 0)
            return (
              <div key={f} className="flex items-center justify-between text-[11px] px-1.5 py-0.5 rounded hover:bg-neutral-800/30">
                <span className="text-neutral-400">{f}</span>
                <span className="text-neutral-500 font-mono">{rows.length}x {formatTokens(inp)}</span>
              </div>
            )
          })}
        </div>
      </div>

      {/* Model breakdown */}
      <div>
        <div className="text-[10px] uppercase tracking-wide text-neutral-500 mb-1.5">By Model</div>
        <div className="space-y-0.5">
          {models.map(m => {
            const rows = usage.filter(r => r.model === m)
            return (
              <div key={m} className="flex items-center justify-between text-[11px] px-1.5 py-0.5 rounded hover:bg-neutral-800/30">
                <span className="text-neutral-400">{MODEL_SHORT[m] || m}</span>
                <span className="text-neutral-500 font-mono">{rows.length} calls</span>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// ─── Conversation Stats Bar ─────────────────────────────────────────────────

function ConversationStats({ messages, toolCallCount }: { messages: MessageData[]; toolCallCount: number }) {
  const userMsgs = messages.filter(m => m.role === 'user').length
  const agentMsgs = messages.filter(m => m.role === 'assistant').length
  const systemMsgs = messages.filter(m => m.role === 'system').length
  const duration = messages.length >= 2 ? messages[messages.length - 1].createdAt - messages[0].createdAt : 0
  const attachmentCount = messages.reduce((s, m) => s + ((m.attachments as any[])?.length || 0), 0)

  return (
    <div className="flex items-center gap-3 text-[11px] px-1 flex-wrap">
      <span className="text-neutral-500">{messages.length} messages</span>
      <span className="text-cyan-500/70">{userMsgs} user</span>
      <span className="text-emerald-500/70">{agentMsgs} agent</span>
      {systemMsgs > 0 && <span className="text-amber-500/70">{systemMsgs} system</span>}
      <span className="text-violet-500/70">{toolCallCount} tools</span>
      {attachmentCount > 0 && <span className="text-neutral-500">{attachmentCount} attachments</span>}
      <span className="text-neutral-600">|</span>
      <span className="text-neutral-500">{formatDuration(duration)}</span>
    </div>
  )
}

// ─── Main Page ──────────────────────────────────────────────────────────────

export default function AdminProjectInspectPage() {
  const { id } = useParams<{ id: string }>()
  const project = useQuery(api.admin.getProject, id ? { id } : 'skip')
  const messages = useQuery(
    api.admin.getProjectMessages,
    project?._id ? { workflowId: project._id } : 'skip'
  ) as MessageData[] | undefined
  const clips = useQuery(
    api.admin.getProjectClips,
    project?._id ? { workflowId: project._id } : 'skip'
  )
  const llmUsage = useQuery(
    api.admin.getProjectLlmUsage,
    project?._id ? { workflowId: project._id } : 'skip'
  )
  const feedback = useQuery(
    api.exportFeedback.getForWorkflow,
    project?._id ? { workflowId: project._id } : 'skip'
  )
  const workflowDoc = useQuery(
    api.workflows.get,
    project?._id ? { id: project._id } : 'skip'
  )

  // State
  const [mainView, setMainView] = useState<'conversation' | 'timeline'>('conversation')
  const [rightPanelOpen, setRightPanelOpen] = useState(true)
  const [rightTab, setRightTab] = useState<'video' | 'clips' | 'llm' | 'raw'>('video')
  const [searchQuery, setSearchQuery] = useState('')
  const [collapsedMessages, setCollapsedMessages] = useState<Set<string>>(new Set())
  const [allCollapsed, setAllCollapsed] = useState(false)
  const [filterRole, setFilterRole] = useState<'all' | 'user' | 'assistant' | 'system' | 'tools'>('all')
  const [highlightedMsg, setHighlightedMsg] = useState<string | null>(null)
  const [wrapLines, setWrapLines] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)

  // Computed
  const filteredMessages = useMemo(() => {
    if (!messages) return []
    let filtered = messages
    if (filterRole === 'user') filtered = filtered.filter(m => m.role === 'user')
    else if (filterRole === 'assistant') filtered = filtered.filter(m => m.role === 'assistant')
    else if (filterRole === 'system') filtered = filtered.filter(m => m.role === 'system')
    else if (filterRole === 'tools') filtered = filtered.filter(m => (m.toolCalls?.length || 0) > 0)

    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      filtered = filtered.filter(m =>
        m.content.toLowerCase().includes(q) ||
        m.toolCalls?.some(tc =>
          tc.name.toLowerCase().includes(q) ||
          JSON.stringify(tc.input).toLowerCase().includes(q)
        ) ||
        m.toolResults?.some(tr =>
          JSON.stringify(tr.result).toLowerCase().includes(q)
        )
      )
    }
    return filtered
  }, [messages, filterRole, searchQuery])

  const toolCallCount = useMemo(
    () => (messages || []).reduce((s, m) => s + (m.toolCalls?.length || 0), 0),
    [messages]
  )

  const jumpToMessage = useCallback((msgId: string) => {
    const el = document.getElementById(`msg-${msgId}`)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      setHighlightedMsg(msgId)
      setTimeout(() => setHighlightedMsg(null), 2000)
    }
  }, [])

  const jumpToNextUser = useCallback((direction: 'next' | 'prev') => {
    if (!filteredMessages.length || !messagesContainerRef.current) return
    const container = messagesContainerRef.current
    const scrollTop = container.scrollTop

    // Find current position
    let currentIdx = -1
    for (let i = 0; i < filteredMessages.length; i++) {
      const el = document.getElementById(`msg-${filteredMessages[i]._id}`)
      if (el && el.offsetTop > scrollTop + 50) {
        currentIdx = i
        break
      }
    }
    if (currentIdx === -1) currentIdx = filteredMessages.length - 1

    // Find next/prev user message
    if (direction === 'next') {
      for (let i = currentIdx; i < filteredMessages.length; i++) {
        if (filteredMessages[i].role === 'user') {
          jumpToMessage(filteredMessages[i]._id)
          return
        }
      }
    } else {
      for (let i = Math.min(currentIdx - 1, filteredMessages.length - 1); i >= 0; i--) {
        if (filteredMessages[i].role === 'user') {
          jumpToMessage(filteredMessages[i]._id)
          return
        }
      }
    }
  }, [filteredMessages, jumpToMessage])

  const toggleCollapse = useCallback((msgId: string) => {
    setCollapsedMessages(prev => {
      const next = new Set(prev)
      if (next.has(msgId)) next.delete(msgId)
      else next.add(msgId)
      return next
    })
  }, [])

  const toggleAllCollapsed = useCallback(() => {
    if (!messages) return
    if (allCollapsed) {
      setCollapsedMessages(new Set())
      setAllCollapsed(false)
    } else {
      setCollapsedMessages(new Set(messages.filter(m => m.role === 'assistant').map(m => m._id)))
      setAllCollapsed(true)
    }
  }, [messages, allCollapsed])

  const exportConversation = useCallback(() => {
    if (!messages || !project) return
    const data = {
      project: { id: project._id, shortcode: project.shortcode, title: project.title },
      exportedAt: new Date().toISOString(),
      messages: messages.map(m => ({
        role: m.role,
        content: m.content,
        timestamp: m.createdAt,
        toolCalls: m.toolCalls,
        toolResults: m.toolResults,
        attachments: m.attachments,
        suggestedActions: m.suggestedActions,
      })),
    }
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `project-${project.shortcode || project._id}-conversation.json`
    a.click()
    URL.revokeObjectURL(url)
  }, [messages, project])

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
      if (e.key === 'j' || e.key === 'ArrowDown') {
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault()
          jumpToNextUser('next')
        }
      }
      if (e.key === 'k' || e.key === 'ArrowUp') {
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault()
          jumpToNextUser('prev')
        }
      }
      if (e.key === '/' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault()
        document.getElementById('inspect-search')?.focus()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [jumpToNextUser])

  // ─── Loading states ─────────────────────────────────────────────────────

  if (project === undefined) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-64px)]">
        <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
      </div>
    )
  }

  if (project === null) {
    return (
      <div className="p-6">
        <Link to="/admin/videos" className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-700 mb-6">
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>
        <p className="text-sm text-neutral-500 text-center py-8">Video not found</p>
      </div>
    )
  }

  const planStyle = PLAN_STYLES[project.effectivePlan] ?? PLAN_STYLES.free

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] bg-neutral-950 text-neutral-200">
      {/* ─── Top Bar ────────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 px-4 py-2 border-b border-neutral-800 bg-neutral-900/80 shrink-0">
        <Link to="/admin/videos" className="text-neutral-500 hover:text-neutral-300 transition-colors">
          <ArrowLeft className="h-4 w-4" />
        </Link>
        {project.thumbnailUrl ? (
          <img src={project.thumbnailUrl} alt="" className="w-8 h-8 rounded object-cover shrink-0" />
        ) : (
          <div className="w-8 h-8 rounded bg-neutral-800 flex items-center justify-center shrink-0">
            <FolderOpen className="h-3.5 w-3.5 text-neutral-600" />
          </div>
        )}
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-neutral-200 truncate">{project.title || 'Untitled'}</span>
            {project.shortcode && <span className="text-[10px] font-mono text-neutral-600">{project.shortcode}</span>}
            <span className={cn('text-[9px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wide', planStyle.bg, planStyle.text)}>
              {planStyle.label}
            </span>
          </div>
          <div className="text-[11px] text-neutral-600">
            {project.userEmail} &middot; {formatDate(project.createdAt)}
          </div>
        </div>

        <span className="flex-1" />

        {/* Quick stats */}
        {messages && (
          <ConversationStats messages={messages} toolCallCount={toolCallCount} />
        )}

        <div className="flex items-center gap-1">
          <a
            href={`/video/${project.shortcode ?? project._id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 px-2.5 py-1 rounded bg-neutral-800 text-neutral-400 text-[11px] hover:bg-neutral-700 hover:text-neutral-200 transition-colors"
          >
            <ExternalLink className="h-3 w-3" /> Open
          </a>
<button
            onClick={exportConversation}
            className="flex items-center gap-1 px-2.5 py-1 rounded bg-neutral-800 text-neutral-400 text-[11px] hover:bg-neutral-700 hover:text-neutral-200 transition-colors"
            title="Export conversation JSON"
          >
            <Download className="h-3 w-3" /> Export
          </button>
          <button
            onClick={() => setRightPanelOpen(!rightPanelOpen)}
            className="flex items-center gap-1 px-2.5 py-1 rounded bg-neutral-800 text-neutral-400 text-[11px] hover:bg-neutral-700 hover:text-neutral-200 transition-colors"
          >
            {rightPanelOpen ? <PanelRightClose className="h-3 w-3" /> : <PanelRightOpen className="h-3 w-3" />}
          </button>
        </div>
      </div>

      {/* ─── Main Content ───────────────────────────────────────────────────── */}
      <div className="flex flex-1 overflow-hidden">
        {/* ─── Left: Conversation / Timeline ──────────────────────────────── */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* View tabs */}
          <div className="flex items-center border-b border-neutral-800 bg-neutral-900/80 shrink-0">
            {([
              { id: 'conversation' as const, label: 'Conversation', icon: MessageCircle },
              { id: 'timeline' as const, label: 'Timeline', icon: Layers },
            ]).map(t => (
              <button
                key={t.id}
                onClick={() => setMainView(t.id)}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-2 text-[11px] font-medium border-b-2 transition-colors',
                  mainView === t.id
                    ? 'border-neutral-400 text-neutral-200'
                    : 'border-transparent text-neutral-500 hover:text-neutral-400'
                )}
              >
                <t.icon className="h-3 w-3" />
                {t.label}
              </button>
            ))}
          </div>

          {mainView === 'timeline' ? (
            workflowDoc && clips ? (
              <div className="flex-1 min-h-0 overflow-hidden">
                <ErrorBoundary level="section" resetKey={project._id}>
                  <WorkflowDocument
                    workflowId={project._id as any}
                    workflow={workflowDoc as any}
                    clips={clips as any}
                    isAdmin
                    timelineOnly
                  />
                </ErrorBoundary>
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <Loader2 className="h-4 w-4 animate-spin text-neutral-600" />
              </div>
            )
          ) : (
          <>
          {/* Search & Filter Bar */}
          <div className="flex items-center gap-2 px-4 py-2 border-b border-neutral-800 bg-neutral-900/50 shrink-0">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-neutral-600" />
              <input
                id="inspect-search"
                type="text"
                placeholder="Search messages, tool calls... (press /)"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-8 pr-8 py-1.5 rounded bg-neutral-800/80 border border-neutral-700/50 text-xs text-neutral-300 placeholder:text-neutral-600 focus:outline-none focus:border-neutral-600"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-600 hover:text-neutral-400">
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {/* Role filter */}
            <div className="flex items-center gap-0.5">
              {([
                { id: 'all' as const, label: 'All' },
                { id: 'user' as const, label: 'User' },
                { id: 'assistant' as const, label: 'Agent' },
                { id: 'system' as const, label: 'System' },
                { id: 'tools' as const, label: 'Tools' },
              ]).map(f => (
                <button
                  key={f.id}
                  onClick={() => setFilterRole(f.id)}
                  className={cn(
                    'px-2 py-1 rounded text-[10px] font-medium transition-colors',
                    filterRole === f.id
                      ? 'bg-neutral-700 text-neutral-200'
                      : 'text-neutral-500 hover:text-neutral-300 hover:bg-neutral-800'
                  )}
                >
                  {f.label}
                </button>
              ))}
            </div>

            <div className="h-4 w-px bg-neutral-800" />

            {/* Actions */}
            <button
              onClick={toggleAllCollapsed}
              className="flex items-center gap-1 px-2 py-1 rounded text-[10px] text-neutral-500 hover:text-neutral-300 hover:bg-neutral-800 transition-colors"
              title={allCollapsed ? 'Expand all' : 'Collapse all agent messages'}
            >
              {allCollapsed ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
              {allCollapsed ? 'Expand' : 'Collapse'}
            </button>

            <button
              onClick={() => setWrapLines(!wrapLines)}
              className={cn(
                "flex items-center gap-1 px-2 py-1 rounded text-[10px] transition-colors",
                wrapLines ? 'text-neutral-200 bg-neutral-700' : 'text-neutral-500 hover:text-neutral-300 hover:bg-neutral-800'
              )}
              title={wrapLines ? 'Disable line wrap' : 'Wrap long lines'}
            >
              <WrapText className="h-3 w-3" />
              Wrap
            </button>

            <button
              onClick={() => jumpToNextUser('prev')}
              className="flex items-center gap-1 px-2 py-1 rounded text-[10px] text-neutral-500 hover:text-neutral-300 hover:bg-neutral-800 transition-colors"
              title="Previous user message (Ctrl+K)"
            >
              <ArrowUp className="h-3 w-3" />
            </button>
            <button
              onClick={() => jumpToNextUser('next')}
              className="flex items-center gap-1 px-2 py-1 rounded text-[10px] text-neutral-500 hover:text-neutral-300 hover:bg-neutral-800 transition-colors"
              title="Next user message (Ctrl+J)"
            >
              <ArrowDown className="h-3 w-3" />
            </button>

            {searchQuery && filteredMessages.length !== (messages?.length || 0) && (
              <span className="text-[10px] text-neutral-600">
                {filteredMessages.length} / {messages?.length || 0}
              </span>
            )}
          </div>

          {/* Messages */}
          <div ref={messagesContainerRef} className="flex-1 overflow-y-auto">
            {!messages ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="h-4 w-4 animate-spin text-neutral-600" />
              </div>
            ) : filteredMessages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-neutral-600 gap-2">
                <MessageSquare className="h-8 w-8" />
                <span className="text-sm">{searchQuery ? 'No messages match your search' : 'No messages'}</span>
              </div>
            ) : (
              <div className="divide-y divide-neutral-800/50">
                {filteredMessages.map((msg, i) => (
                  <MessageBlock
                    key={msg._id}
                    msg={msg}
                    index={messages.indexOf(msg)}
                    prevTimestamp={i > 0 ? filteredMessages[i - 1].createdAt : null}
                    collapsed={collapsedMessages.has(msg._id)}
                    onToggleCollapse={() => toggleCollapse(msg._id)}
                    searchQuery={searchQuery}
                    isHighlighted={highlightedMsg === msg._id}
                    wrapLines={wrapLines}
                  />
                ))}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>
          </>
          )}
        </div>

        {/* ─── Right: Panel ───────────────────────────────────────────────── */}
        {rightPanelOpen && (
          <div className="w-[380px] shrink-0 border-l border-neutral-800 bg-neutral-900/50 flex flex-col overflow-hidden">
            {/* Panel tabs */}
            <div className="flex items-center border-b border-neutral-800 shrink-0">
              {([
                { id: 'video' as const, label: 'Video', icon: Film },
                { id: 'clips' as const, label: `Clips (${clips?.length || 0})`, icon: Layers },
                { id: 'llm' as const, label: 'LLM', icon: Cpu },
                { id: 'raw' as const, label: 'Raw', icon: Database },
              ]).map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setRightTab(tab.id)}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-2 text-[11px] font-medium border-b-2 transition-colors',
                    rightTab === tab.id
                      ? 'border-neutral-400 text-neutral-200'
                      : 'border-transparent text-neutral-500 hover:text-neutral-400'
                  )}
                >
                  <tab.icon className="h-3 w-3" />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Panel content */}
            <div className="flex-1 overflow-y-auto p-3">
              {rightTab === 'video' && (
                <div className="space-y-3">
                  {/* Video player */}
                  <div className="rounded-lg overflow-hidden bg-black">
                    {project.hasExported && project.finalVideoUrl ? (
                      <PlyrPlayer
                        src={project.finalVideoUrl}
                        poster={project.thumbnailUrl || undefined}
                        className="w-full"
                      />
                    ) : project.thumbnailUrl ? (
                      <div className="relative aspect-video">
                        <img src={project.thumbnailUrl} alt="" className="w-full h-full object-contain" />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                          <span className="text-[11px] text-white/70 bg-black/50 px-2.5 py-1 rounded-full">
                            {project.hasExported ? 'Processing...' : 'No export'}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="aspect-video flex items-center justify-center">
                        <Film className="h-8 w-8 text-neutral-800" />
                      </div>
                    )}
                  </div>

                  {/* Project stats */}
                  <div className="space-y-2">
                    <div className="text-[10px] uppercase tracking-wide text-neutral-500">Video Info</div>
                    <div className="grid grid-cols-2 gap-1.5 text-[11px]">
                      <div className="rounded bg-neutral-800/50 px-2.5 py-1.5">
                        <span className="text-neutral-500">Status</span>
                        <div className="text-neutral-300 capitalize">{project.status}</div>
                      </div>
                      <div className="rounded bg-neutral-800/50 px-2.5 py-1.5">
                        <span className="text-neutral-500">Clips</span>
                        <div className="text-neutral-300">{project.generatedClipCount}/{project.clipCount}</div>
                      </div>
                      <div className="rounded bg-neutral-800/50 px-2.5 py-1.5">
                        <span className="text-neutral-500">Duration</span>
                        <div className="text-neutral-300">{formatDuration(project.sessionDurationMs)}</div>
                      </div>
                      <div className="rounded bg-neutral-800/50 px-2.5 py-1.5">
                        <span className="text-neutral-500">Export</span>
                        <div className="text-neutral-300">{project.hasExported ? 'Done' : project.finalVideoStatus || 'None'}</div>
                      </div>
                    </div>

                    {project.narrative && (
                      <div className="rounded bg-neutral-800/50 px-2.5 py-2">
                        <div className="text-[10px] text-neutral-500 mb-1">Narrative</div>
                        <div className="text-[11px] text-neutral-400 leading-relaxed">{project.narrative}</div>
                      </div>
                    )}

                    {feedback && feedback.filter(f => !f.skipped).length > 0 && (
                      <div className="rounded bg-neutral-800/50 px-2.5 py-2 space-y-1.5">
                        <div className="text-[10px] text-neutral-500">Feedback</div>
                        {feedback.filter(f => !f.skipped).map((fb) => (
                          <div key={fb._id}>
                            <div className="flex items-center gap-0.5">
                              {[1, 2, 3, 4, 5].map((s) => (
                                <Star key={s} size={10} className={s <= fb.rating ? 'fill-amber-400 text-amber-400' : 'text-neutral-600'} />
                              ))}
                              <span className="text-[10px] text-neutral-500 ml-1">{fb.rating}/5</span>
                            </div>
                            {fb.feedback && (
                              <p className="text-[11px] text-neutral-400 leading-relaxed mt-0.5">{fb.feedback}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {rightTab === 'clips' && (
                clips ? (
                  clips.length > 0 ? (
                    <ClipInspector clips={clips} />
                  ) : (
                    <div className="text-center text-neutral-600 text-sm py-8">No clips</div>
                  )
                ) : (
                  <div className="flex justify-center py-8"><Loader2 className="h-4 w-4 animate-spin text-neutral-600" /></div>
                )
              )}

              {rightTab === 'llm' && (
                llmUsage ? (
                  llmUsage.length > 0 ? (
                    <LlmUsagePanel usage={llmUsage} />
                  ) : (
                    <div className="text-center text-neutral-600 text-sm py-8">No LLM usage data</div>
                  )
                ) : (
                  <div className="flex justify-center py-8"><Loader2 className="h-4 w-4 animate-spin text-neutral-600" /></div>
                )
              )}

              {rightTab === 'raw' && (
                <div className="space-y-3">
                  <div className="text-[10px] uppercase tracking-wide text-neutral-500">Video Data</div>
                  <div className={cn("rounded bg-neutral-900 p-2 font-mono text-[10px] max-h-[calc(100vh-200px)] overflow-y-auto", wrapLines ? 'whitespace-pre-wrap break-all' : 'overflow-x-auto')}>
                    <JsonValue value={{
                      _id: project._id,
                      shortcode: project.shortcode,
                      title: project.title,
                      status: project.status,
                      userEmail: project.userEmail,
                      userName: project.userName,
                      effectivePlan: project.effectivePlan,
                      clipCount: project.clipCount,
                      generatedClipCount: project.generatedClipCount,
                      failedClipCount: project.failedClipCount,
                      messageCount: project.messageCount,
                      userMessageCount: project.userMessageCount,
                      agentMessageCount: project.agentMessageCount,
                      toolCallCount: project.toolCallCount,
                      sessionDurationMs: project.sessionDurationMs,
                      hasScript: project.hasScript,
                      hasCharacters: project.hasCharacters,
                      hasGenerated: project.hasGenerated,
                      hasExported: project.hasExported,
                      finalVideoStatus: project.finalVideoStatus,
                      createdAt: project.createdAt,
                      updatedAt: project.updatedAt,
                    }} />
                  </div>

                  {messages && (
                    <>
                      <div className="text-[10px] uppercase tracking-wide text-neutral-500 mt-4">Message Timing</div>
                      <div className="space-y-0.5">
                        {messages.map((m, i) => {
                          const delta = i > 0 ? m.createdAt - messages[i - 1].createdAt : 0
                          const isLong = delta > 30_000
                          return (
                            <button
                              key={m._id}
                              onClick={() => jumpToMessage(m._id)}
                              className="w-full flex items-center gap-1.5 px-1.5 py-0.5 rounded text-[10px] font-mono hover:bg-neutral-800/50 text-left"
                            >
                              <span className={cn(
                                'w-1.5 h-1.5 rounded-full shrink-0',
                                m.role === 'user' ? 'bg-cyan-500' : m.role === 'assistant' ? 'bg-emerald-500' : 'bg-amber-500'
                              )} />
                              <span className="text-neutral-600 w-6">#{i + 1}</span>
                              <span className="text-neutral-500">{formatTime(m.createdAt)}</span>
                              {delta > 0 && (
                                <span className={cn('text-neutral-700', isLong && 'text-amber-600')}>
                                  +{formatDelta(delta)}
                                </span>
                              )}
                              {(m.toolCalls?.length || 0) > 0 && (
                                <span className="text-violet-600">{m.toolCalls!.length}T</span>
                              )}
                            </button>
                          )
                        })}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
